import { type LoaderFunctionArgs } from '@remix-run/node';
import { Button } from 'flowbite-react';
import { useState } from 'react';
import { useLocale } from 'remix-i18next/react';
import { typedjson, useTypedLoaderData, useTypedRouteLoaderData } from 'remix-typedjson';
import { getCountryName } from '#common/countries';
import { prisma } from '#common/db.server';
import { useEditableTranslation } from '#common/i18n/edit-mode.tsx';
import { useTranslatedPath } from '#common/utils/i18n.tsx';
import { ContactFormDialog } from '#speaker-shop/components/ContactFormDialog.tsx';
import { useCoreLegalTheme } from '#speaker-shop/components/CoreLegalPageLayout.tsx';
import { FaceLayout } from '#speaker-shop/components/FaceLayout.tsx';
import { NavigateToHome } from '#speaker-shop/components/NavigateToHome.tsx';
import { getLocale } from '#speaker-shop/i18n/i18n.server';
import { getSpeakerShopData } from '#speaker-shop/models/speaker-shop-data.server';
import { type loader as rootLoader } from '#speaker-shop/root';
import { resolveVoiceAndLangParams } from '#speaker-shop/utils/resolve-voice';
import { resolveShopContext } from '#speaker-shop/utils/shop-context.server.ts';

export const handle = {
    i18n: [ 'imprint' ]
};

export async function loader({ request, params }: LoaderFunctionArgs) {
    const { voiceSlug, lang } = resolveVoiceAndLangParams(params);
    const shopContext = await resolveShopContext(request.url, voiceSlug);
    const speaker = shopContext.unifiedShopPage && shopContext.speakerId
        ? await prisma.speaker.findUniqueOrThrow({
            where: { id: shopContext.speakerId },
            select: { privacyMode: true, imprint: true }
        })
        : (await getSpeakerShopData(request.url, voiceSlug))?.speakerVoice.speaker;
    if (!speaker) {
        return typedjson({ privacyMode: false, imprint: null });
    }

    const privacyMode = speaker?.privacyMode ?? false;
    const imprint = speaker?.imprint;

    if (!imprint && !privacyMode) {
        throw new Response('No imprint data found', { status: 404 });
    }

    const locale = await getLocale(null, lang, request);

    return typedjson({
        privacyMode,
        imprint: !privacyMode && imprint
            ? {
                legalName: imprint.legalName,
                street: imprint.street,
                zipCode: imprint.zipCode,
                city: imprint.city,
                country: (imprint.countryCode && getCountryName(imprint.countryCode, locale)) ?? imprint.country
            }
            : null
    });
}

export default function Imprint() {
    const { imprint } = useTypedLoaderData<typeof loader>();
    const { t } = useEditableTranslation('imprint');
    const { t: tCommon } = useEditableTranslation('common');
    const translatePath = useTranslatedPath(useLocale());
    const rootData = useTypedRouteLoaderData<typeof rootLoader>('root')!;
    const coreTheme = useCoreLegalTheme();
    const [ showArtistContact, setShowArtistContact ] = useState(false);
    const [ showPlatformContact, setShowPlatformContact ] = useState(false);
    if ('unifiedShopPage' in rootData && rootData.unifiedShopPage) {
        if (!coreTheme) {
            throw new Error('Unified imprint must be rendered inside CoreLegalPageLayout');
        }
        return (
            <div className='core-legal-page'>
                <a className='core-back-button mb-8' href={translatePath('/')}>← {tCommon('navigate-to-home')}</a>
                <h1 className='core-legal-title'>{t('title')}</h1>
                {imprint && (
                    <section className='core-legal-section'>
                        <div className='core-legal-address'>
                            <p>{imprint.legalName}</p>
                            <p>{imprint.street}</p>
                            <p>{imprint.zipCode} {imprint.city}</p>
                            <p>{imprint.country}</p>
                        </div>
                        <button
                            className='core-secondary-button mt-6'
                            type='button'
                            onClick={() => setShowArtistContact(true)}
                        >
                            {t('contact-artist')}
                        </button>
                    </section>
                )}
                <section className='core-legal-section'>
                    <h2>{t('responsible-for-platform')}</h2>
                    <div className='core-legal-address'>
                        <p>CrispyVoice GmbH</p>
                        <p>Bösch 23</p>
                        <p>6331 Hünenberg</p>
                        <p>Switzerland</p>
                    </div>
                    <button
                        className='core-secondary-button mt-6'
                        type='button'
                        onClick={() => setShowPlatformContact(true)}
                    >
                        {t('contact-platform')}
                    </button>
                </section>
                <ContactFormDialog
                    open={showArtistContact}
                    onClose={() => setShowArtistContact(false)}
                    topic='artist-contact'
                    description={t('contact-artist-description')}
                    appearance='unified'
                    theme={coreTheme}
                />
                <ContactFormDialog
                    open={showPlatformContact}
                    onClose={() => setShowPlatformContact(false)}
                    topic='platform-contact'
                    description={t('contact-platform-description')}
                    appearance='unified'
                    theme={coreTheme}
                />
            </div>
        );
    }
    return (
        <div className='flex flex-col h-full'>
            <FaceLayout faceSize='small'>
                <div className='p-20 lg:p-0'>
                    <NavigateToHome className='justify-center lg:justify-start' />
                    <h1 className='font-display text-4xl md:text-5xl font-extrabold text-slate-900 text-center lg:text-left mb-8'>
                        {t('title')}
                    </h1>
                    {imprint && (
                        <div className='text-lg items-center flex flex-col lg:items-start'>
                            <p>{imprint.legalName}</p>
                            <p>{imprint.street}</p>
                            <p>{imprint.zipCode} {imprint.city}</p>
                            <p>{imprint.country}</p>
                            <Button
                                className='mt-6 rounded'
                                color={'crispybluedark'}
                                size='lg'
                                onClick={() => setShowArtistContact(true)}
                            >
                                {t('contact-artist')}
                            </Button>
                        </div>
                    )}
                    <h1
                        className={`${
                            imprint ? 'mt-16' : ''
                        } font-display text-4xl md:text-5xl font-extrabold text-slate-900 text-center lg:text-left mb-8`}
                    >
                        {t('responsible-for-platform')}
                    </h1>
                    <div className='text-lg items-center flex flex-col lg:items-start'>
                        <p>CrispyVoice GmbH</p>
                        <p>Bösch 23</p>
                        <p>6331 Hünenberg</p>
                        <p>Switzerland</p>
                        <Button
                            className='mt-6 rounded'
                            color={'crispybluedark'}
                            size='lg'
                            onClick={() => setShowPlatformContact(true)}
                        >
                            {t('contact-platform')}
                        </Button>
                    </div>
                </div>
            </FaceLayout>
            <ContactFormDialog
                open={showArtistContact}
                onClose={() => setShowArtistContact(false)}
                topic='artist-contact'
                description={t('contact-artist-description')}
            />
            <ContactFormDialog
                open={showPlatformContact}
                onClose={() => setShowPlatformContact(false)}
                topic='platform-contact'
                description={t('contact-platform-description')}
            />
        </div>
    );
}
